import TemplateDto from '@/services/skills/Dto/TemplateDto';
import PageTemplateDto from '@/services/skills/Dto/PageTemplateDto';
import TemplateRequestDto from '@/services/skills/Dto/TemplateRequestDto';
import TemplatesApiRepository from '@/services/skills/TemplatesApiRepository';
import AgentSettingsService from '@/services/account/agentSettingsService';
import { buildDelayKey } from '@/components/Skills/LixsaSkills/ThanksOrder/Utils/SendDelayConfig';
import { v4 as uuidv4 } from 'uuid';

const FLOW_TYPE = 'THANKS_ORDER';
const THANKS_ORDER_SKILL_ID = 'f4281b3e-9719-4f96-8d22-2d68b31fdcc2';
const CATEGORY = 'UTILITY';

const state = () => ({
    templatesList: null,
    currentLanguage: 'en',
    currentTemplate: null,
    availableLanguages: [],
    /**
     * `config_values` de la skill Thanks Order tal cual los devuelve el backend
     * (mapa plano de strings). Aquí viven las claves `delay_minutes_*` del retardo
     * de envío de cada plantilla. Ver Utils/SendDelayConfig.js.
     */
    skillConfig: {},
});

const formatLanguage = (language) => {
    if (!language) return '';
    const [base, region] = language.split('_');
    return region ? `${base.toLowerCase()}_${region.toUpperCase()}` : base.toLowerCase();
};

const getters = {
    getCurrentTemplate: (state) => state.currentTemplate,
    getSkillConfig: (state) => state.skillConfig,
    /**
     * Retardo de envío (en minutos) de una plantilla. 0 = al instante.
     * @returns {function(string): number}
     */
    getDelayMinutes: (state) => (templateName) => {
        if (!templateName) return 0;
        return Math.max(0, Math.floor(Number(state.skillConfig[buildDelayKey(templateName)] || 0)));
    },
    getCurrentLanguage: (state) => state.currentLanguage,
    getAvailableLanguages: (state) => state.availableLanguages,
    currentTemplateIsSavedInTemplatesList: (state) => {
        if (!state.currentTemplate) return false;

        return state.templatesList.content.some((template) => template.uuid === state.currentTemplate.uuid);
    },
    getCurrentTemplateStatus: (state) => {
        if (!state.currentTemplate) return null;

        return state.currentTemplate.status || 'NEW';
    },
};

const mutations = {
    SET_SKILL_CONFIG(state, configValues) {
        state.skillConfig = { ...(configValues || {}) };
    },
    SET_DELAY_MINUTES(state, { templateName, minutes }) {
        // Nueva referencia para que Vue 2 vea el cambio de una clave nueva.
        state.skillConfig = { ...state.skillConfig, [buildDelayKey(templateName)]: String(minutes) };
    },
    SET_TEMPLATE_LIST(state, templatesList) {
        state.templatesList = templatesList;
    },
    SET_CURRENT_LANGUAGE(state, language) {
        state.currentLanguage = language;
    },
    SET_AVAILABLE_LANGUAGES(state, languages) {
        state.availableLanguages = languages;
    },
    SET_CURRENT_TEMPLATE(state, template) {
        state.currentTemplate = template;
    },
    // Modified by Martin - 2026-07-02 — Optimistic update with new array reference for Vue reactivity
    UPDATE_TEMPLATE_IN_LIST(state, { uuid, templateContent, templateName, buttons }) {
        if (state.currentTemplate && state.currentTemplate.uuid === uuid) {
            state.currentTemplate = {
                ...state.currentTemplate,
                body: templateContent,
                ...(templateName ? { templateName } : {}),
                ...(buttons !== undefined ? { buttons } : {}),
            };
        }
        if (state.templatesList?.content) {
            state.templatesList = {
                ...state.templatesList,
                content: state.templatesList.content.map((t) =>
                    t.uuid === uuid
                        ? {
                              ...t,
                              body: templateContent,
                              ...(templateName ? { templateName } : {}),
                              ...(buttons !== undefined ? { buttons } : {}),
                          }
                        : t,
                ),
            };
        }
    },
};

const actions = {
    async fetchTemplatesList({ commit }) {
        try {
            const templates = await TemplatesApiRepository.getTemplates(FLOW_TYPE);
            commit('SET_TEMPLATE_LIST', templates);
            return templates;
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    },

    async fetchCurrentTemplate({ commit, state, dispatch }) {
        const foundTemplate = state.templatesList?.content?.find(
            (template) => formatLanguage(template.language) === formatLanguage(state.currentLanguage),
        );
        if (foundTemplate) {
            const existingTemplate = new TemplateDto(foundTemplate);
            commit('SET_CURRENT_TEMPLATE', existingTemplate);
            return;
        }

        try {
            await dispatch('fetchDefaultTemplate');
        } catch (error) {
            console.error('Error fetching current template:', error);
        }
    },

    async fetchDefaultTemplate({ commit, state }) {
        try {
            const templateVariables = await TemplatesApiRepository.getTemplateVariables(
                THANKS_ORDER_SKILL_ID,
                FLOW_TYPE,
                state.currentLanguage,
            );
            const existingTemplate = new TemplateDto.Builder()
                .withUuid(templateVariables.uuid)
                .withTemplateType(templateVariables.templateType)
                .withFlowType(templateVariables.flowType)
                .withSkillId(templateVariables.skillId)
                .withVariables([templateVariables])
                .withBody(templateVariables.contentExample)
                .withLanguage(templateVariables.language)
                .withDateCreated(templateVariables.dateCreated)
                .withDateUpdated(templateVariables.dateUpdated)
                .build();

            commit('SET_CURRENT_TEMPLATE', existingTemplate);
        } catch (error) {
            console.error('Error fetching default template:', error);
            throw error;
        }
    },

    async fetchAvailableLanguages({ commit }) {
        try {
            const languages = await TemplatesApiRepository.getAvailableLanguages();
            commit('SET_AVAILABLE_LANGUAGES', languages);
        } catch (error) {
            console.error('Error fetching available languages:', error);
            throw error;
        }
    },

    async fetchCurrentLanguage({ rootGetters, state, commit }) {
        const hasShopifySkill = rootGetters['agentSettings/hasShopifySkill'];
        if (!hasShopifySkill) {
            commit('SET_CURRENT_LANGUAGE', 'en');
            return;
        }

        const shopifySkill = rootGetters['agentSettings/getSkillByInternalName']('Shopify');
        const shopifyLanguage = shopifySkill?.config_values?.countryCode;

        const language = state.availableLanguages.find((language) => language.tag === shopifyLanguage);

        commit('SET_CURRENT_LANGUAGE', language || 'en');
    },

    async updateTemplate(
        { commit, state, getters, dispatch, rootState },
        { templateContent, templateName, accountId, buttons, businessAccountId },
    ) {
        try {
            const isEditing = getters.currentTemplateIsSavedInTemplatesList;

            if (isEditing && state.currentTemplate) {
                let shopDomain = rootState.user.account?.shop_public_domain || 'lixsa.ai';
                shopDomain = shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');

                const formattedButtons = (buttons || []).map((btn) => {
                    if (btn.type === 'URL' && btn.url_type === 'DYNAMIC') {
                        // Workaround: Meta requires dynamic URLs to start with https:// and only end with a variable {{1}}
                        const cleanBtn = { ...btn, example: [`https://${shopDomain}/redirect`] };
                        cleanBtn.url = `https://${shopDomain}/{{1}}`;
                        return cleanBtn;
                    }
                    return btn;
                });

                // Update template
                const templateRequest = new TemplateRequestDto(state.currentTemplate);
                templateRequest.body = templateContent;
                if (templateName) {
                    templateRequest.template_name = templateName;
                }
                templateRequest.buttons = formattedButtons;
                await TemplatesApiRepository.updateTemplate(state.currentTemplate.uuid, templateRequest);
                commit('UPDATE_TEMPLATE_IN_LIST', {
                    uuid: state.currentTemplate.uuid,
                    templateContent,
                    templateName,
                    buttons: formattedButtons,
                });
            } else {
                let shopDomain = rootState.user.account?.shop_public_domain || 'lixsa.ai';
                shopDomain = shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');

                // Create template
                const formattedButtons = (buttons || []).map((btn) => {
                    if (btn.type === 'URL' && btn.url_type === 'DYNAMIC') {
                        // Workaround: Meta requires dynamic URLs to start with https:// and only end with a variable {{1}}
                        const cleanBtn = { ...btn, example: [`https://${shopDomain}/redirect`] };
                        cleanBtn.url = `https://${shopDomain}/{{1}}`;
                        return cleanBtn;
                    }
                    return btn;
                });

                const templateRequest = new TemplateRequestDto.Builder()
                    .withUuid(uuidv4())
                    .withAccountId(accountId)
                    .withBody(templateContent)
                    .withLanguage(formatLanguage(state.currentLanguage))
                    .withTemplateName(templateName || 'thanks_order')
                    .withCategory(CATEGORY)
                    .withType('TEXT')
                    .withTemplateType('FLOW')
                    .withFlowType(FLOW_TYPE)
                    .withSkillId(THANKS_ORDER_SKILL_ID)
                    .withChannelType('WHATSAPP')
                    .withButtons(formattedButtons)
                    .withBusinessAccountId(businessAccountId)
                    .build();
                await TemplatesApiRepository.createTemplate(templateRequest);
                await dispatch('fetchTemplatesList');
            }
        } catch (error) {
            console.error('Error updating template:', error);
            const errorMsg = error.response?.data?.message || error.response?.data || error.message || 'Unknown error';
            throw new Error(`[Backend Error]: ${JSON.stringify(errorMsg)}`);
        }
    },
    async deleteTemplate({ commit, state }, templateUuid) {
        try {
            const foundTemplate = state.templatesList.content.find((template) => template.uuid === templateUuid);
            if (foundTemplate) {
                const templateRequest = new TemplateRequestDto(foundTemplate);
                await TemplatesApiRepository.deleteTemplate(templateUuid, templateRequest);

                // Optimistic UI update: Remove from local list immediately
                const updatedContent = state.templatesList.content.filter((t) => t.uuid !== templateUuid);
                const updatedList = new PageTemplateDto({
                    ...state.templatesList,
                    content: updatedContent,
                    numberOfElements: updatedContent.length,
                });
                commit('SET_TEMPLATE_LIST', updatedList);
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    },
    /**
     * Lee los `config_values` de la skill Thanks Order (de donde sale el retardo de envío).
     */
    async fetchSkillConfig({ commit }) {
        try {
            const response = await AgentSettingsService.getAgentSettings();
            const skills = response.data?.skills || [];
            const thanksOrderSkill = skills.find((skill) => skill.skill_id === THANKS_ORDER_SKILL_ID);
            commit('SET_SKILL_CONFIG', thanksOrderSkill?.config_values || {});
        } catch (error) {
            console.error('Error fetching thanks order skill config:', error);
        }
    },

    /**
     * Guarda el retardo de envío de una plantilla. Va por PATCH (`updateSkillConfigValues`),
     * que fusiona claves y no choca con el `config_schema` vacío de la skill: el POST de
     * `modifyAgentSettings` sí lo haría, y además reescribiría `enabled`.
     *
     * @param {{ templateName: string, minutes: number }} payload
     */
    async saveDelayMinutes({ commit }, { templateName, minutes }) {
        const safeMinutes = Math.max(0, Math.floor(Number(minutes) || 0));
        try {
            await AgentSettingsService.updateSkillConfigValues(THANKS_ORDER_SKILL_ID, {
                [buildDelayKey(templateName)]: String(safeMinutes),
            });
            commit('SET_DELAY_MINUTES', { templateName, minutes: safeMinutes });
        } catch (error) {
            console.error('Error saving thanks order send delay:', error);
            throw error;
        }
    },

    setCurrentLanguage({ commit }, language) {
        commit('SET_CURRENT_LANGUAGE', formatLanguage(language));
    },

    selectTemplate({ commit }, template) {
        commit('SET_CURRENT_TEMPLATE', new TemplateDto(template));
    },

    async subscribeToTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        try {
            await dispatch(
                'websocket/subscribe',
                { topic, callback: (data) => dispatch('handleTemplateEvent', data) },
                { root: true },
            );
        } catch (error) {
            console.error(`Failed to subscribe to ${topic}:`, error);
        }
    },

    unsubscribeFromTemplateUpdates({ rootState, dispatch }) {
        const topic = `/account/${rootState.user.account.uuid}/template`;
        dispatch('websocket/unsubscribe', { topic }, { root: true });
    },

    handleTemplateEvent({ commit, state, dispatch }, event) {
        if (!event || !event.data) return;

        // Parse JSON string if it's a string
        const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        // In some events like TemplateUpdated, the data IS the template.
        // In TemplateScoringUpdated, the user spec says 'data' has the 'template' object fields directly.
        // So eventData is effectively our template source.
        const templateSource = eventData;

        // Create template using builder pattern to ensure proper object construction
        const template = new TemplateDto.Builder()
            .withUuid(templateSource.uuid)
            .withAccountId(templateSource.accountId || templateSource.account_id)
            .withBody(templateSource.body)
            .withHeader(templateSource.header)
            .withChannelType(templateSource.channelType || templateSource.channel_type)
            .withTemplateName(templateSource.templateName || templateSource.template_name)
            .withLanguage(templateSource.language)
            .withCategory(templateSource.category)
            .withStatus(templateSource.status)
            .withScore(templateSource.score)
            .withType(templateSource.type)
            .withTemplateType(templateSource.templateType || templateSource.template_type)
            .withFlowType(templateSource.flowType || templateSource.flow_type)
            .withSkillId(templateSource.skillId || templateSource.skill_id)
            .withInternalTemplateId(templateSource.internalTemplateId || templateSource.internal_template_id)
            .withTemplateParameters(templateSource.templateParameters || templateSource.template_parameters)
            .withExtraParameters(templateSource.extraParameters || templateSource.extra_parameters)
            .withIssues(templateSource.issues)
            .withModifyBy(templateSource.modifyBy || templateSource.modify_by)
            .withBusinessAccountId(templateSource.businessAccountId || templateSource.business_account_id)
            .withDateCreated(templateSource.dateCreated || templateSource.date_created)
            .withDateUpdated(templateSource.dateUpdated || templateSource.date_updated)
            .withVariables(templateSource.variables || [])
            .withButtons(templateSource.buttons || [])
            .build();

        if (!state.templatesList) return;

        // Create a shallow copy of the templates list and its content array to ensure reactivity
        const updatedTemplatesList = { ...state.templatesList };
        updatedTemplatesList.content = [...state.templatesList.content];

        const templateIndex = updatedTemplatesList.content.findIndex((t) => t.uuid === template.uuid);

        switch (event.name) {
            case 'TemplateUpdated':
                // Find and update the template in the list
                if (templateIndex !== -1) {
                    updatedTemplatesList.content[templateIndex] = template;
                    commit('SET_TEMPLATE_LIST', updatedTemplatesList);

                    // If current template is the updated one, update it directly
                    if (state.currentTemplate?.uuid === template.uuid) {
                        dispatch('fetchCurrentTemplate');
                    }
                }
                break;
            case 'TemplateScoringUpdated':
                if (templateIndex !== -1) {
                    const existingTemplate = updatedTemplatesList.content[templateIndex];
                    // Update only the score found in the event (merged into existing)
                    // We create a new object to ensure reactivity
                    // Use the score from the constructed 'template' object which came from event data
                    updatedTemplatesList.content[templateIndex] = {
                        ...existingTemplate,
                        score: template.score,
                    };

                    commit('SET_TEMPLATE_LIST', updatedTemplatesList);

                    // If current template is the updated one, we should also update it
                    if (state.currentTemplate?.uuid === template.uuid) {
                        // We can either fetch again or just update local state if we trust the event
                        // Fetching is safer to get full consistent state
                        dispatch('fetchCurrentTemplate');
                    }
                }
                break;
            default:
                console.warn(`Unhandled template event: ${event.name}`);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
